"use client";

import { useEffect, useRef } from "react";
import { Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { trackImpression } from "@/src/features/demo-contracts/analytics";

/* -------------------------------------------------------------------------- */
/*  Props                                                                     */
/* -------------------------------------------------------------------------- */

export interface GuidedLandingProps {
  className?: string;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function GuidedLanding({ className }: GuidedLandingProps) {
  const impressionTracked = useRef(false);

  /* ---- Track impression once on mount ---- */
  useEffect(() => {
    if (!impressionTracked.current) {
      trackImpression();
      impressionTracked.current = true;
    }
  }, []);

  function handleJumpToPromptBox() {
    window.requestAnimationFrame(() => {
      document.getElementById("guided-prompt-box")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-primary px-6 py-24 lg:px-8 lg:py-32",
        className,
      )}
    >
      {/* Evidence-wall atmosphere */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* 1px rule at top */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
        {/* Faint grid overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          {/* Launch badge — monospace label */}
          <div className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.04] px-3 py-1">
            <Users className="size-3 text-accent" aria-hidden="true" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/65">
              For founders tired of building in the dark
            </span>
          </div>

          <h1 className="mx-auto mt-6 max-w-4xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl xl:text-7xl">
            <span className="text-balance">
              You can ship for months and still hear{" "}
              <span className="text-accent">
                &quot;I don&apos;t need this.&quot;
              </span>
            </span>
          </h1>

          {/* Supporting copy — tighter, monospace metadata */}
          <p className="mx-auto mt-5 max-w-2xl font-mono text-sm leading-[1.7] text-white/65 sm:text-base lg:text-xl">
            Most startup ideas fail before launch because validation happens too
            late. Use the guided prompt box below to pick a niche, preview
            deterministic pain signals, and decide if the idea is worth
            building.
          </p>

          {/* Metadata accent line */}
          <div className="mx-auto mt-6 max-w-xs flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">
              30-second preview
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="mt-8 lg:mt-10">
            <Button
              size="lg"
              onClick={handleJumpToPromptBox}
              className="h-12 bg-accent px-8 text-base font-semibold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/25"
            >
              Inspect a niche
            </Button>
            <p className="mt-2 font-mono text-[11px] text-white/55">
              No sign-up required. Pick a niche, see demand signals, decide.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
